import { useState } from 'react';
import { Box, Paper, Tabs, Tab, styled } from '@mui/material';
import { useErrorHandler } from '../utils/useErrorHandler';
import { useFlowManagement } from './hooks/useFlowManagement';
import { useFlowStates } from './hooks/useFlowStates';
import { useFlowLocations } from './hooks/useFlowLocations';
import { useResizeHandle } from './hooks/useResizeHandle';
import { getLocationIndexDisplay } from './utils/flowUtils';
import ProcessFlowsSidebar from './components/ProcessFlowsSidebar';
import FlowDetails from './components/FlowDetails';
import FlowStatesTab from './components/FlowStatesTab';
import LocationsTab from './components/LocationsTab';
import Modals from './components/Modals';
import { ResizeHandle, StyledSidebar } from '../conf/constants';

const MainSection = styled(Paper)(({ }) => ({
  flexGrow: 1,
  height: 'calc(100vh - 64px)',
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
}));

const FlowStateManagement = () => {
  const { snackbar, handleSnackbarClose, showSuccessMessage, handleErrorWithLogout } = useErrorHandler();
  const { leftSidebarWidth, isResizing, setIsResizing, containerRef } = useResizeHandle();
  const [selectedTab, setSelectedTab] = useState(0);

  // Flow Management
  const {
    processFlows,
    selectedFlow,
    setSelectedFlow,
    flowEditData,
    setFlowEditData,
    newFlowData,
    setNewFlowData,
    openCreateFlowModal,
    setOpenCreateFlowModal,
    handleUploadIcon,
    handleCreateFlow,
    handleDeleteFlow,
    handleUpdateFlow,
  } = useFlowManagement({ showSuccessMessage, handleErrorWithLogout });

  // Flow States Management
  const {
    flowStates,
    selectedState,
    setSelectedState,
    flowStateConfig,
    setFlowStateConfig,
    newFlowStateData,
    setNewFlowStateData,
    openAddFlowStateModal,
    setOpenAddFlowStateModal,
    handleUploadStateIcon,
    handleDeleteFlowState,
    handleUpdateFlowState,
    handleAddFlowState,
  } = useFlowStates(selectedFlow, { showSuccessMessage, handleErrorWithLogout });

  // Flow Locations Management
  const {
    flowLocations,
    selectedLocation,
    setSelectedLocation,
    locationConfig,
    setLocationConfig,
    openModal,
    setOpenModal,
    newLocationName,
    setNewLocationName,
    isMoving,
    handleAddLocation,
    handleRemoveLocation,
    handleMoveLocation,
  } = useFlowLocations(selectedFlow, { showSuccessMessage, handleErrorWithLogout });

  return (
    <Box
      sx={{ display: 'flex', height: '100%' }}
      ref={containerRef}
      style={{ cursor: isResizing ? 'col-resize' : 'auto' }}
    >
      <StyledSidebar
        elevation={0}
        sidebarWidth={leftSidebarWidth}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 64px)',
        }}
      >
        <ProcessFlowsSidebar
          processFlows={processFlows}
          selectedFlow={selectedFlow}
          setSelectedFlow={setSelectedFlow}
          handleDeleteFlow={handleDeleteFlow}
          setOpenCreateFlowModal={setOpenCreateFlowModal}
        />
      </StyledSidebar>

      <ResizeHandle onMouseDown={() => setIsResizing(true)} />

      <MainSection elevation={0}>
        <FlowDetails
          handleUploadIcon={handleUploadIcon}
          flowEditData={flowEditData}
          setFlowEditData={setFlowEditData}
          selectedFlowData={processFlows.find((flow) => flow.id === selectedFlow)}
          handleUpdateFlow={handleUpdateFlow}
        />
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
          <Tab label="States" />
          <Tab label="Locations" />
        </Tabs>
        <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex' }}>
          {selectedTab === 0 && (
            <FlowStatesTab
              handleUploadStateIcon={handleUploadStateIcon}
              flowStates={flowStates}
              selectedState={selectedState}
              setSelectedState={setSelectedState}
              flowStateConfig={flowStateConfig}
              setFlowStateConfig={setFlowStateConfig}
              flowLocations={flowLocations}
              handleDeleteFlowState={handleDeleteFlowState}
              handleUpdateFlowState={handleUpdateFlowState}
              setOpenAddFlowStateModal={setOpenAddFlowStateModal}
              getLocationIndexDisplay={(index) => getLocationIndexDisplay(index, flowLocations)}
            />
          )}
          {selectedTab === 1 && (
            <LocationsTab
              flowLocations={flowLocations}
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              locationConfig={locationConfig}
              setLocationConfig={setLocationConfig}
              handleRemoveLocation={handleRemoveLocation}
              setOpenModal={setOpenModal}
              isMoving={isMoving}
              canMoveUp={(index) => index > 0}
              canMoveDown={(index) => index < flowLocations.length - 1}
              handleMoveLocation={handleMoveLocation}
            />
          )}
        </Box>
        <Modals
          openModal={openModal}
          setOpenModal={setOpenModal}
          newLocationName={newLocationName}
          setNewLocationName={setNewLocationName}
          handleAddLocation={handleAddLocation}
          openAddFlowStateModal={openAddFlowStateModal}
          setOpenAddFlowStateModal={setOpenAddFlowStateModal}
          newFlowStateData={newFlowStateData}
          setNewFlowStateData={setNewFlowStateData}
          handleAddFlowState={handleAddFlowState}
          openCreateFlowModal={openCreateFlowModal}
          setOpenCreateFlowModal={setOpenCreateFlowModal}
          newFlowData={newFlowData}
          setNewFlowData={setNewFlowData}
          handleCreateFlow={handleCreateFlow}
          snackbar={snackbar}
          handleSnackbarClose={handleSnackbarClose}
        />
      </MainSection>
    </Box>
  );
};

export default FlowStateManagement;